import { useState } from 'react';
import { Link } from 'react-router-dom';
import { SHRTLogo } from '@/components/SHRTLogo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  ArrowLeft, 
  Loader2, 
  Send, 
  CheckCircle,
  User,
  Mail,
  Phone,
  MapPin,
  Car,
  FileText
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MembershipRequestPage = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    street: '',
    zip: '',
    city: '',
    country: 'Schweiz',
    vehicles: '',
    motivation: '',
    referral: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.first_name || !formData.last_name || !formData.email) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus');
      return;
    }
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Bitte geben Sie eine gültige E-Mail-Adresse ein');
      return;
    }
    
    setLoading(true);
    
    try {
      await axios.post(`${API_URL}/api/membership-request`, formData);
      setSubmitted(true);
      toast.success('Ihre Anfrage wurde erfolgreich gesendet');
    } catch (error) {
      const message = error.response?.data?.detail || 'Fehler beim Senden der Anfrage';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Success state
  if (submitted) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        {/* Header */}
        <header className="border-b border-border bg-card">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/login" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
              <SHRTLogo size="sm" />
              <div>
                <p className="font-['Oswald'] text-sm font-bold tracking-wider">S.H.R.T.</p>
                <p className="text-[10px] text-muted-foreground uppercase tracking-widest">Swiss Historic Racing Team</p>
              </div>
            </Link>
          </div>
        </header>
        
        {/* Success Content */}
        <main className="flex-1 flex items-center justify-center p-6">
          <div className="max-w-md text-center">
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
            <h1 className="font-['Oswald'] text-3xl font-bold uppercase tracking-wide mb-4">
              Vielen Dank!
            </h1>
            <p className="text-muted-foreground mb-6">
              Ihre Mitgliedsanfrage wurde erfolgreich übermittelt. 
              Wir werden uns in Kürze bei Ihnen melden.
            </p>
            <Link to="/login">
              <Button variant="outline" className="rounded-none uppercase tracking-wider">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Zurück zum Login
              </Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/login" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <SHRTLogo size="sm" />
            <div>
              <p className="font-['Oswald'] text-sm font-bold tracking-wider">S.H.R.T.</p>
              <p className="text-[10px] text-muted-foreground uppercase tracking-widest">Swiss Historic Racing Team</p>
            </div>
          </Link>
          <Link 
            to="/login" 
            className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
          >
            <ArrowLeft className="w-4 h-4" />
            Zurück zum Login
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-2xl mx-auto px-6 py-12">
        {/* Title */}
        <div className="text-center mb-10">
          <h1 className="font-['Oswald'] text-4xl font-bold uppercase tracking-wide mb-3">
            Mitglied werden
          </h1>
          <p className="text-muted-foreground">
            Werden Sie Teil des Swiss Historic Racing Team und teilen Sie Ihre Leidenschaft für historische Rennfahrzeuge.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Personal Information */}
          <div className="bg-card border border-border p-6">
            <h2 className="font-['Oswald'] text-lg font-bold uppercase tracking-wide mb-6 flex items-center gap-2">
              <User className="w-5 h-5 text-primary" />
              Persönliche Angaben
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  Vorname *
                </Label>
                <Input
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  placeholder="Max"
                  className="mt-1 rounded-none"
                  required
                  data-testid="first-name-input"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  Nachname *
                </Label>
                <Input
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  placeholder="Muster"
                  className="mt-1 rounded-none"
                  required
                  data-testid="last-name-input"
                />
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-card border border-border p-6">
            <h2 className="font-['Oswald'] text-lg font-bold uppercase tracking-wide mb-6 flex items-center gap-2">
              <Mail className="w-5 h-5 text-primary" />
              Kontaktdaten
            </h2>
            
            <div className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  E-Mail *
                </Label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="max.muster@example.ch"
                  className="mt-1 rounded-none"
                  required
                  data-testid="email-input"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  Telefon
                </Label>
                <Input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+41 79 123 45 67"
                  className="mt-1 rounded-none"
                  data-testid="phone-input"
                />
              </div>
            </div>
          </div>

          {/* Address */}
          <div className="bg-card border border-border p-6">
            <h2 className="font-['Oswald'] text-lg font-bold uppercase tracking-wide mb-6 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-primary" />
              Adresse
            </h2>
            
            <div className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  Strasse / Nr.
                </Label>
                <Input
                  name="street"
                  value={formData.street}
                  onChange={handleChange}
                  placeholder="Musterstrasse 123"
                  className="mt-1 rounded-none"
                  data-testid="street-input"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                    PLZ
                  </Label>
                  <Input
                    name="zip"
                    value={formData.zip}
                    onChange={handleChange}
                    placeholder="8000"
                    className="mt-1 rounded-none"
                    data-testid="zip-input"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                    Ort
                  </Label>
                  <Input
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="Zürich"
                    className="mt-1 rounded-none"
                    data-testid="city-input"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  Land
                </Label>
                <Input
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  className="mt-1 rounded-none"
                  data-testid="country-input"
                />
              </div>
            </div>
          </div>

          {/* Vehicles & Motivation */}
          <div className="bg-card border border-border p-6">
            <h2 className="font-['Oswald'] text-lg font-bold uppercase tracking-wide mb-6 flex items-center gap-2">
              <Car className="w-5 h-5 text-primary" />
              Fahrzeuge & Motivation
            </h2>
            
            <div className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  Ihre Fahrzeuge
                </Label>
                <Textarea
                  name="vehicles"
                  value={formData.vehicles}
                  onChange={handleChange}
                  placeholder="z.B. Porsche 911 Carrera RS 2.7 (1973), Ferrari 250 GTO (1962)..."
                  className="mt-1 rounded-none"
                  rows={3}
                  data-testid="vehicles-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Welche historischen Fahrzeuge besitzen Sie?
                </p>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  Motivation
                </Label>
                <Textarea
                  name="motivation"
                  value={formData.motivation}
                  onChange={handleChange}
                  placeholder="Warum möchten Sie Mitglied werden?"
                  className="mt-1 rounded-none"
                  rows={3}
                  data-testid="motivation-input"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  Wie haben Sie von uns erfahren?
                </Label>
                <Input
                  name="referral"
                  value={formData.referral}
                  onChange={handleChange}
                  placeholder="z.B. Empfehlung von Hans Muster"
                  className="mt-1 rounded-none"
                  data-testid="referral-input"
                />
              </div>
            </div>
          </div>

          {/* Submit */}
          <div className="flex flex-col items-center gap-4 pt-4">
            <Button
              type="submit"
              size="lg"
              disabled={loading}
              className="w-full md:w-auto rounded-none uppercase tracking-wider px-12"
              data-testid="submit-request-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Wird gesendet...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5 mr-2" />
                  Anfrage senden
                </>
              )}
            </Button>
            <p className="text-xs text-muted-foreground text-center max-w-md">
              Mit dem Absenden stimmen Sie zu, dass wir Ihre Daten zur Bearbeitung Ihrer Anfrage verwenden.
            </p>
          </div>
        </form>
      </main>

      {/* Footer */}
      <footer className="border-t border-border mt-12 py-6">
        <div className="max-w-4xl mx-auto px-6 text-center text-sm text-muted-foreground">
          <div className="flex justify-center gap-4 mb-2">
            <Link to="/agb" className="hover:text-foreground">AGB</Link>
            <span>|</span>
            <Link to="/impressum" className="hover:text-foreground">Impressum</Link>
          </div>
          © {new Date().getFullYear()} Swiss Historic Racing Team
        </div>
      </footer>
    </div>
  );
};

export default MembershipRequestPage;
